import { ref, reactive, computed, watch, nextTick } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import StoryEngine from "@osuecampus/branching-stories";
import { loadLabEntryModule, ACTIVE_LAB_ID } from "@/labRegistry.js";

/**
 * @param {unknown} a
 * @param {unknown} b
 */
function visitedPassageIdsEqual(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * Next `visitedPassageIds` for this snapshot: `[current]` when unset, else append
 * `current` when missing (or unchanged when already listed).
 * @param {object} context
 * @returns {string[] | null} null when there is no current passage
 */
function computeVisitedPassageIds(context) {
  const current = context?.current;
  if (!current) return null;
  const raw = context.flags?.visitedPassageIds;
  const prev = Array.isArray(raw) ? raw : [];

  if (prev.length === 0) {
    return [current];
  }
  if (prev.includes(current)) return prev;
  return [...prev, current];
}

/**
 * Loads lab workbook JSON, owns `StoryEngine` lifecycle, and syncs Vuex context.
 * @param {object} [options]
 * @param {import('vuex').Store} [options.store]
 * @param {import('vue-router').Router} [options.router]
 * @param {string} [options.labId] Defaults to `ACTIVE_LAB_ID`.
 * @param {(snapshot: { context: object; value: unknown }) => void} [options.onAfterStateChange] Persist + completion checks.
 */
export function useStoryEngine(options = {}) {
  const store = options.store ?? useStore();
  const router = options.router ?? useRouter();
  const labId = options.labId ?? ACTIVE_LAB_ID;
  const onAfterStateChange = options.onAfterStateChange ?? (() => {});

  const stories = reactive({});
  const storyService = ref(null);
  const storyContext = ref({});
  const storyState = ref("");
  const storyDataLoaded = ref(false);

  const productionScenes = computed(() => stories[labId]?.draft || []);

  const labManifest = computed(() => store.getters.labManifest);

  function applySnapshot(snapshot) {
    storyContext.value = snapshot.context;
    storyState.value = snapshot.value;
    store.dispatch("setContext", snapshot.context);
  }

  /**
   * Ensures `context.current` is recorded in `visitedPassageIds` via `updateFlags` when needed.
   * When `updateFlags` runs, skips `onAfterStateChange` for this tick so persistence
   * sees the updated flags on the follow-up snapshot.
   * @returns {boolean} true if `updateFlags` was sent (defer persistence callback)
   */
  function syncVisitedPassageIds(snapshot) {
    const engine = storyService.value;
    if (!engine) return false;
    const next = computeVisitedPassageIds(snapshot.context);
    if (next === null) return false;
    const prev = Array.isArray(snapshot.context.flags?.visitedPassageIds)
      ? snapshot.context.flags.visitedPassageIds
      : [];
    if (visitedPassageIdsEqual(next, prev)) return false;
    engine.updateFlags({ visitedPassageIds: next });
    return true;
  }

  function handleStoryStateChange(snapshot) {
    storyContext.value = snapshot.context;
    storyState.value = snapshot.value;
    store.dispatch("setContext", snapshot.context);
    const deferPersistence = syncVisitedPassageIds(snapshot);
    if (!deferPersistence) {
      onAfterStateChange(snapshot);
    }
  }

  function subscribeStateChange() {
    storyService.value.onStateChange(handleStoryStateChange);
  }

  async function loadStoryData(lid) {
    if (!lid) return;
    if (stories[lid]) {
      storyDataLoaded.value = true;
      await initializeStory();
      return;
    }
    try {
      const labModule = await loadLabEntryModule(lid);
      stories[lid] = labModule.default;
      store.dispatch("setLabManifest", labModule.labManifest ?? null);
      storyDataLoaded.value = true;
      await initializeStory();
    } catch (err) {
      console.error(`Failed to load lab data for "${lid}":`, err);
      storyDataLoaded.value = false;
      store.dispatch("setLabManifest", null);
      store.dispatch("setStoryEngineActive", false);
    }
  }

  async function initializeStory() {
    if (!storyDataLoaded.value || !labId) {
      console.warn("Story data not loaded");
      store.dispatch("setStoryEngineActive", false);
      return;
    }
    // `entryLoaded` lives on datastore module state, not getters (see store/datastore.js).
    const entryLoaded = store.state.datastore?.entryLoaded === true;
    if (store.getters.useDatastore && !entryLoaded) {
      console.warn("Entry not loaded");
      store.dispatch("setStoryEngineActive", false);
      return;
    }

    if (storyService.value) {
      storyService.value.stop();
      storyService.value = null;
      storyContext.value = {};
      storyState.value = "";
      store.dispatch("setStoryEngineActive", false);
      await nextTick();
    }

    if (
      !store.getters.getCurrentLabAttempt &&
      store.getters.canStartNewAttempt
    ) {
      try {
        await store.dispatch("startNewLabAttempt");
      } catch (error) {
        console.error("Cannot start new lab attempt:", error.message);
        store.dispatch("setStoryEngineActive", false);
        router.push({ name: "LabAttempts" });
        return;
      }
    }

    const ctx = store.getters.context;

    if (ctx && ctx.current) {
      const passageExists = productionScenes.value.some(
        (passage) => passage.id === ctx.current
      );

      if (passageExists) {
        try {
          storyService.value = new StoryEngine(productionScenes.value, ctx);

          const snapshot = storyService.value.getSnapshot();
          applySnapshot(snapshot);
          subscribeStateChange();
          const deferInitial = syncVisitedPassageIds(snapshot);
          if (!deferInitial) {
            onAfterStateChange(snapshot);
          }
          store.dispatch("setStoryEngineActive", true);
          return;
        } catch (error) {
          console.error(
            "Error initializing story with existing context:",
            error
          );
        }
      } else {
        console.warn(
          `Invalid passage ID "${ctx.current}" found in context, starting fresh story`
        );
      }
    }

    console.log("Starting fresh story with ID:", labId);
    storyService.value = new StoryEngine(productionScenes.value);

    const freshSnapshot = storyService.value.getSnapshot();
    applySnapshot(freshSnapshot);
    subscribeStateChange();
    const deferFresh = syncVisitedPassageIds(freshSnapshot);
    if (!deferFresh) {
      onAfterStateChange(freshSnapshot);
    }
    store.dispatch("setStoryEngineActive", true);
  }

  watch(
    () => labId,
    (newLabId) => {
      if (newLabId) {
        if (!stories[newLabId]) {
          storyDataLoaded.value = false;
        }
        loadStoryData(newLabId);
      } else {
        storyDataLoaded.value = false;
      }
    },
    { immediate: true }
  );

  return {
    stories,
    storyService,
    storyContext,
    storyState,
    storyDataLoaded,
    productionScenes,
    labManifest,
    loadStoryData,
    initializeStory
  };
}
